import { useEffect, useState } from 'react'
import { COLORS, TYPE, SPACE } from './tokens'
import SectionLabel from './SectionLabel'
import { computeProgressPercent } from './useInsuranceReviewPipeline'

function StageCircle({ state, index }) {
  const bg = state === 'done' ? COLORS.accent : COLORS.bodyBg
  const color = state === 'done' ? COLORS.retentionBg : COLORS.mutedText
  const border = state === 'active' ? COLORS.accent : COLORS.hairline
  const borderWidth = state === 'active' ? 2 : 1.5
  return (
    <div style={{
      width: '36px', height: '36px', borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg,
      border: `${borderWidth}px solid ${border}`,
      color,
      fontWeight: 600, fontSize: '14px'
    }}>
      {state === 'done' ? '✓' : state === 'active' ? (
        <span style={{
          display: 'inline-block', width: '20px', height: '20px',
          border: `2px solid ${COLORS.hairline}`, borderTopColor: COLORS.accent,
          borderRadius: '50%', animation: 'ir-spin 900ms linear infinite'
        }} />
      ) : String(index)}
    </div>
  )
}

function StageRow({ name, state, statusText, elapsedText }) {
  const nameColor = state === 'pending' ? COLORS.mutedText : COLORS.bodyText
  return (
    <li style={{ display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: '16px', alignItems: 'baseline', padding: '12px 0' }}>
      <span style={{
        width: '20px', height: '20px', borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '11px', fontWeight: 700,
        background: state === 'done' ? COLORS.accent : 'transparent',
        border: `1.5px solid ${state === 'done' || state === 'active' ? COLORS.accent : COLORS.hairline}`,
        color: state === 'done' ? COLORS.retentionBg : COLORS.mutedText
      }}>
        {state === 'done' ? '✓' : state === 'active' ? '⟳' : ''}
      </span>
      <div>
        <div style={{ ...TYPE.smallCaps, color: nameColor }}>{name}</div>
        <div style={{ ...TYPE.body, color: COLORS.mutedText, marginTop: '2px' }}>{statusText}</div>
      </div>
      <div style={{ ...TYPE.supporting, color: COLORS.mutedText, fontVariantNumeric: 'tabular-nums' }}>
        {elapsedText}
      </div>
    </li>
  )
}

function formatElapsed(ms) {
  if (ms == null || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return `${m}m ${String(rem).padStart(2, '0')}s`
}

export default function PipelineTracker({ stages, runStartedAt, activeStageStartedAt }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const elapsedMs = activeStageStartedAt ? now - activeStageStartedAt : 0
  const totalElapsedMs = runStartedAt ? now - runStartedAt : 0
  const pct = computeProgressPercent(stages, elapsedMs)

  const analystStatus = stages.analyst === 'done' ? 'Extracted policy structure' : stages.analyst === 'active' ? 'Extracting policy structure…' : 'Waiting'
  const specialistStatus = stages.specialist === 'done' ? 'Analyzed coverage adequacy' : stages.specialist === 'active' ? 'Analyzing coverage adequacy against BIA and threat profile…' : 'Waiting'
  const qaStatus = stages.qa === 'done' ? 'Verified citations and voice' : stages.qa === 'active' ? 'Verifying citations and voice…' : 'Waiting'

  return (
    <section style={{ maxWidth: SPACE.sectionMaxWidth, margin: '0 auto', padding: `48px ${SPACE.pagePadding}` }}>
      <style>{`@keyframes ir-spin { to { transform: rotate(360deg); } }`}</style>
      <SectionLabel>Running Specialist review</SectionLabel>

      <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', alignItems: 'center', margin: '32px 0' }}>
        <div style={{ position: 'absolute', top: '50%', left: '5%', right: '5%', height: '1px', background: COLORS.hairline }} />
        <div style={{ position: 'absolute', top: '50%', left: '5%', width: `${(pct / 100) * 90}%`, height: '1px', background: COLORS.accent, transition: 'width 200ms ease' }} />
        <div style={{ display: 'flex', justifyContent: 'center', zIndex: 2 }}><StageCircle state={stages.analyst} index={1} /></div>
        <div style={{ display: 'flex', justifyContent: 'center', zIndex: 2 }}><StageCircle state={stages.specialist} index={2} /></div>
        <div style={{ display: 'flex', justifyContent: 'center', zIndex: 2 }}><StageCircle state={stages.qa} index={3} /></div>
      </div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        <StageRow name="Analyst" state={stages.analyst} statusText={analystStatus} elapsedText={stages.analyst === 'active' ? formatElapsed(elapsedMs) : stages.analyst === 'done' ? formatElapsed(8000) : 'waiting'} />
        <StageRow name="Specialist" state={stages.specialist} statusText={specialistStatus} elapsedText={stages.specialist === 'active' ? formatElapsed(elapsedMs) : stages.specialist === 'done' ? '~2m' : 'waiting'} />
        <StageRow name="QA" state={stages.qa} statusText={qaStatus} elapsedText={stages.qa === 'active' ? formatElapsed(elapsedMs) : stages.qa === 'done' ? '~2m' : 'est. 2m'} />
      </ul>

      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ ...TYPE.smallCaps, color: COLORS.mutedText }}>Progress · elapsed {formatElapsed(totalElapsedMs)}</span>
          <span style={{ ...TYPE.hero, color: COLORS.bodyText }}>{pct}%</span>
        </div>
        <div style={{ height: '6px', background: COLORS.hairline, borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: COLORS.accent, width: `${pct}%`, transition: 'width 200ms ease' }} />
        </div>
      </div>
    </section>
  )
}
